import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { FlowCard } from '../components/FlowCard';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import type { ArtifactKind, Flow, TagSummary, Team } from '../types';
import '../styles/marketplace.css';

const PAGE_SIZE_OPTIONS = [12, 24, 48] as const;
const SCROLL_STORAGE_PREFIX = 'marketplace:scroll:';
const FILTERS_STORAGE_KEY = 'marketplace:filters';
const VIEW_MODE_KEY = 'marketplace:viewMode';
const LIST_COLUMNS_KEY = 'marketplace:listColumns';

const ALL_LIST_COLUMNS = [
  { id: 'title', label: 'Title' },
  { id: 'kind', label: 'Kind' },
  { id: 'type', label: 'Type' },
  { id: 'visibility', label: 'Visibility' },
  { id: 'owner', label: 'Owner' },
  { id: 'updated', label: 'Updated' },
  { id: 'status', label: 'Status' },
  { id: 'labels', label: 'Labels' },
  { id: 'upvotes', label: 'Upvotes' },
  { id: 'tags', label: 'Tags' },
] as const;

type ColumnId = (typeof ALL_LIST_COLUMNS)[number]['id'];

const DEFAULT_COLUMNS = new Set<ColumnId>(ALL_LIST_COLUMNS.map((c) => c.id));

function loadVisibleColumns(): Set<ColumnId> {
  try {
    const saved = localStorage.getItem(LIST_COLUMNS_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as string[];
      if (Array.isArray(parsed) && parsed.length > 0) {
        const valid = new Set<ColumnId>();
        for (const id of parsed) {
          if (ALL_LIST_COLUMNS.some((c) => c.id === id)) {
            valid.add(id as ColumnId);
          }
        }
        if (valid.size > 0) return valid;
      }
    }
  } catch { /* ignore */ }
  return new Set(DEFAULT_COLUMNS);
}

function getRestoredSearchParams(): URLSearchParams {
  try {
    const saved = sessionStorage.getItem(FILTERS_STORAGE_KEY);
    if (saved) {
      return new URLSearchParams(saved);
    }
  } catch {
    // ignore
  }
  return new URLSearchParams();
}

export function MarketplacePage() {
  const { user } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams(
    window.location.search === '' ? getRestoredSearchParams() : undefined
  );
  const [flows, setFlows] = useState<Flow[]>([]);
  const [serverTotal, setServerTotal] = useState(0);
  const [serverTotalPages, setServerTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState<string>(() => searchParams.get('search') ?? '');
  const [tags, setTags] = useState<TagSummary[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>(() => {
    const tagParams = searchParams.getAll('tag');
    if (tagParams.length > 0) {
      return Array.from(new Set(tagParams.filter((tag) => tag.trim().length > 0)));
    }
    const legacy = searchParams.get('tags');
    return legacy ? legacy.split(',').map((tag) => tag.trim()).filter(Boolean) : [];
  });
  const [selectedKinds, setSelectedKinds] = useState<{ flow: boolean; node: boolean }>(() => {
    const kindParam = searchParams.get('kind');
    if (kindParam === 'flow') {
      return { flow: true, node: false };
    }
    if (kindParam === 'node') {
      return { flow: false, node: true };
    }
    return { flow: true, node: true };
  });
  const [authorFilter, setAuthorFilter] = useState<string>(() => searchParams.get('author') ?? '');
  const [createdFrom, setCreatedFrom] = useState<string>(() => searchParams.get('createdFrom') ?? '');
  const [createdTo, setCreatedTo] = useState<string>(() => searchParams.get('createdTo') ?? '');
  const [updatedFrom, setUpdatedFrom] = useState<string>(() => searchParams.get('updatedFrom') ?? '');
  const [updatedTo, setUpdatedTo] = useState<string>(() => searchParams.get('updatedTo') ?? '');
  const [showAdvanced, setShowAdvanced] = useState<boolean>(() => searchParams.get('advanced') === '1');
  const [statusFilter, setStatusFilter] = useState<'approved' | 'pending' | 'rejected' | 'all'>(() => {
    const param = searchParams.get('status');
    const allowed = new Set(['approved', 'pending', 'rejected', 'all']);
    if (param && allowed.has(param)) {
      const statusValue = param as 'approved' | 'pending' | 'rejected' | 'all';
      if (statusValue === 'approved' || user?.role === 'admin') {
        return statusValue;
      }
    }
    return user?.role === 'admin' ? 'all' : 'approved';
  });
  const [pageSize, setPageSize] = useState<number>(() => {
    const parsed = Number(searchParams.get('pageSize'));
    return PAGE_SIZE_OPTIONS.includes(parsed as (typeof PAGE_SIZE_OPTIONS)[number])
      ? parsed
      : PAGE_SIZE_OPTIONS[0];
  });
  const [page, setPage] = useState<number>(() => {
    const parsed = Number(searchParams.get('page'));
    return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 1;
  });
  const [sortBy, setSortBy] = useState<'updatedAt' | 'createdAt' | 'title' | 'upvotes'>(() => {
    const param = searchParams.get('sortBy');
    return param === 'createdAt' || param === 'title' || param === 'upvotes' ? param : 'updatedAt';
  });
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(() => {
    const param = searchParams.get('sortOrder');
    return param === 'asc' ? 'asc' : 'desc';
  });
  const [showAllTags, setShowAllTags] = useState<boolean>(() => searchParams.get('showTags') === 'all');
  const [recommendedOnly, setRecommendedOnly] = useState<boolean>(
    () => searchParams.get('recommended') === 'true'
  );
  const [madeByThalesOnly, setMadeByThalesOnly] = useState<boolean>(
    () => searchParams.get('madeByThales') === 'true'
  );
  const [visibilityFilter, setVisibilityFilter] = useState<'all' | 'public' | 'teams'>(() => {
    const param = searchParams.get('visibility');
    if (param === 'public' || param === 'teams') {
      return param;
    }
    return 'all';
  });
  const [myTeams, setMyTeams] = useState<Team[]>([]);
  const [allTeams, setAllTeams] = useState<Team[]>([]);
  const [teamFilterIds, setTeamFilterIds] = useState<string[]>(() => {
    const teamParams = searchParams.getAll('team');
    return teamParams.length > 0 ? Array.from(new Set(teamParams.filter((t) => t.trim().length > 0))) : [];
  });
  const [showOtherTeams, setShowOtherTeams] = useState(false);
  const [filtersCollapsed, setFiltersCollapsed] = useState<boolean>(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => {
    try {
      const saved = localStorage.getItem(VIEW_MODE_KEY);
      if (saved === 'list') return 'list';
    } catch { /* ignore */ }
    return 'grid';
  });
  const [visibleColumns, setVisibleColumns] = useState<Set<ColumnId>>(loadVisibleColumns);
  const [columnPickerOpen, setColumnPickerOpen] = useState(false);
  const columnPickerRef = useRef<HTMLDivElement | null>(null);
  const TAG_PREVIEW_COUNT = 8;
  const autoRefreshTick = useAutoRefresh(5000);
  const requestIdRef = useRef(0);
  const filtersInitializedRef = useRef(false);
  const pendingScrollRestoreRef = useRef<number | null>(null);
  const nextFetchSilentRef = useRef(false);
  const resultScrollAnchorRef = useRef<HTMLDivElement | null>(null);
  const scrollStorageKeyRef = useRef('');
  const scrollPersistedRef = useRef(false);
  const marketplaceLinkState = useMemo(
    () => ({
      from: {
        pathname: location.pathname,
        search: location.search,
        label: 'Marketplace'
      }
    }),
    [location.pathname, location.search]
  );
  const apiKind = useMemo(() => {
    if (selectedKinds.flow === selectedKinds.node) {
      return undefined;
    }
    return selectedKinds.flow ? 'flow' : 'node';
  }, [selectedKinds.flow, selectedKinds.node]);

  const otherTeams = useMemo(() => {
    if (user?.role !== 'admin' || allTeams.length === 0) {
      return [];
    }
    const myTeamIds = new Set(myTeams.map((t) => t.id));
    return allTeams.filter((t) => !myTeamIds.has(t.id));
  }, [allTeams, myTeams, user?.role]);

  const needAllTeams = useMemo(() => {
    if (user?.role !== 'admin' || teamFilterIds.length === 0) {
      return false;
    }
    const myTeamIds = new Set(myTeams.map((t) => t.id));
    return teamFilterIds.some((id) => !myTeamIds.has(id));
  }, [myTeams, teamFilterIds, user?.role]);

  useEffect(() => {
    const key = `${location.pathname}${location.search ?? ''}`;
    scrollStorageKeyRef.current = key;
    scrollPersistedRef.current = false;
    const stored = sessionStorage.getItem(`${SCROLL_STORAGE_PREFIX}${key}`);
    if (stored) {
      const parsed = Number(stored);
      if (!Number.isNaN(parsed)) {
        pendingScrollRestoreRef.current = parsed;
        nextFetchSilentRef.current = true;
      }
    } else {
      pendingScrollRestoreRef.current = null;
    }
  }, [location.pathname, location.search]);

  const persistScrollPosition = useCallback(() => {
  const key = scrollStorageKeyRef.current;
  try {
    sessionStorage.setItem(`${SCROLL_STORAGE_PREFIX}${key}`, String(window.scrollY));
  } catch (err) {
    console.warn('Unable to store marketplace scroll position', err);
  }
  scrollPersistedRef.current = true;
}, []);

  const restorePendingScroll = useCallback(() => {
    const target = pendingScrollRestoreRef.current;
    if (target === null) {
      return;
    }
    const scrollNow = () => window.scrollTo({ top: target });
    requestAnimationFrame(() => {
      scrollNow();
      requestAnimationFrame(scrollNow);
    });
    pendingScrollRestoreRef.current = null;
    scrollPersistedRef.current = false;
    sessionStorage.removeItem(`${SCROLL_STORAGE_PREFIX}${scrollStorageKeyRef.current}`);
  }, []);

  useEffect(
    () => () => {
      if (!scrollPersistedRef.current) {
        persistScrollPosition();
      }
    },
    [persistScrollPosition]
  );

  useEffect(() => {
    let isActive = true;

    (async () => {
      try {
        const [fetchedTags, fetchedTeams, fetchedAllTeams] = await Promise.all([
          api.tags(),
          api.myTeams(),
          user?.role === 'admin' ? api.listTeams({ status: 'active' }) : Promise.resolve([])
        ]);
        if (isActive) {
          setTags(fetchedTags);
          setMyTeams(fetchedTeams.teams || []);
          setAllTeams(Array.isArray(fetchedAllTeams) ? fetchedAllTeams : []);
        }
      } catch (err) {
        console.error('Failed to load tags', err);
      }
    })();

    return () => {
      isActive = false;
    };
  }, [user?.role]);

  useEffect(() => {
    if (user?.role !== 'admin' && statusFilter !== 'approved') {
      setStatusFilter('approved');
    }
  }, [statusFilter, user?.role]);

  useEffect(() => {
    if (filtersInitializedRef.current) {
      setPage(1);
    } else {
      filtersInitializedRef.current = true;
    }
  }, [
    search,
    selectedTags,
    statusFilter,
    selectedKinds.flow,
    selectedKinds.node,
    authorFilter,
    createdFrom,
    createdTo,
    updatedFrom,
    updatedTo,
    sortBy,
    sortOrder,
    recommendedOnly,
    madeByThalesOnly,
    pageSize,
    visibilityFilter,
    teamFilterIds,
    user?.role
  ]);

  useEffect(() => {
    const params = new URLSearchParams();

    const trimmedSearch = search.trim();
    if (trimmedSearch) {
      params.set('search', trimmedSearch);
    }

    selectedTags.forEach((tag) => {
      if (tag.trim().length > 0) {
        params.append('tag', tag);
      }
    });

    if (!selectedKinds.flow || !selectedKinds.node) {
      params.set('kind', selectedKinds.flow ? 'flow' : 'node');
    }

    const trimmedAuthor = authorFilter.trim();
    if (trimmedAuthor) {
      params.set('author', trimmedAuthor);
    }
    if (createdFrom) {
      params.set('createdFrom', createdFrom);
    }
    if (createdTo) {
      params.set('createdTo', createdTo);
    }
    if (updatedFrom) {
      params.set('updatedFrom', updatedFrom);
    }
    if (updatedTo) {
      params.set('updatedTo', updatedTo);
    }
    if (showAdvanced) {
      params.set('advanced', '1');
    }

    if (user?.role === 'admin') {
      if (statusFilter !== 'all') {
        params.set('status', statusFilter);
      }
    } else if (statusFilter !== 'approved') {
      params.set('status', statusFilter);
    }

    if (recommendedOnly) {
      params.set('recommended', 'true');
    }
    if (madeByThalesOnly) {
      params.set('madeByThales', 'true');
    }
    if (visibilityFilter !== 'all') {
      params.set('visibility', visibilityFilter);
    }
    teamFilterIds.forEach((id) => {
      params.append('team', id);
    });
    if (sortBy !== 'updatedAt') {
      params.set('sortBy', sortBy);
    }
    if (sortOrder !== 'desc') {
      params.set('sortOrder', sortOrder);
    }
    if (page > 1) {
      params.set('page', String(page));
    }
    if (pageSize !== PAGE_SIZE_OPTIONS[0]) {
      params.set('pageSize', String(pageSize));
    }
    if (showAllTags) {
      params.set('showTags', 'all');
    }

    const nextQuery = params.toString();
    try {
      sessionStorage.setItem(FILTERS_STORAGE_KEY, nextQuery);
    } catch {
      // ignore
    }
    if (nextQuery !== searchParams.toString()) {
      setSearchParams(params, { replace: true });
    }
  }, [
    authorFilter,
    createdFrom,
    createdTo,
    page,
    pageSize,
    recommendedOnly,
    madeByThalesOnly,
    visibilityFilter,
    teamFilterIds,
    search,
    searchParams,
    selectedKinds.flow,
    selectedKinds.node,
    selectedTags,
    setSearchParams,
    showAdvanced,
    showAllTags,
    sortBy,
    sortOrder,
    statusFilter,
    updatedFrom,
    updatedTo,
    user?.role
  ]);

  const fetchFlows = useCallback(
    async ({ silent = false } = {}) => {
      const requestId = ++requestIdRef.current;

      if (!silent) {
        setLoading(true);
        setError(null);
      }

      try {
        const response = await api.paginatedFlows({
          search: search.trim() || undefined,
          tags: selectedTags,
          status: user?.role === 'admin' && statusFilter !== 'all' ? statusFilter : undefined,
          kind: apiKind,
          author: authorFilter.trim() || undefined,
          createdFrom: createdFrom || undefined,
          createdTo: createdTo || undefined,
          updatedFrom: updatedFrom || undefined,
          updatedTo: updatedTo || undefined,
          page,
          pageSize,
          sortBy,
          sortOrder,
          recommendedOnly: recommendedOnly || undefined,
          madeByThales: madeByThalesOnly || undefined,
          allTeams: needAllTeams,
          visibility: visibilityFilter === 'public' ? 'public' : visibilityFilter === 'teams' ? 'teams' : undefined
        });

        if (requestIdRef.current !== requestId) {
          return;
        }

        if (response.totalPages > 0 && page > response.totalPages) {
          setPage(response.totalPages);
          return;
        }

        const normalized = response.items.map((entry) => {
          const rawKind = typeof entry.kind === 'string' ? entry.kind.trim().toLowerCase() : null;
          const kind: ArtifactKind = rawKind === 'node' ? 'node' : 'flow';
          return { ...entry, kind };
        });

        setFlows(normalized);
        setServerTotal(response.total);
        setServerTotalPages(response.totalPages);
      } catch (err) {
        if (requestIdRef.current !== requestId) {
          return;
        }

        if (silent) {
          console.error('Marketplace background refresh failed', err);
        } else {
          setError((err as Error).message);
        }
      } finally {
        if (requestIdRef.current !== requestId) {
          return;
        }

        setLoading(false);
        restorePendingScroll();
      }
    },
    [
      apiKind,
      authorFilter,
      createdFrom,
      createdTo,
      page,
      pageSize,
      recommendedOnly,
      madeByThalesOnly,
      needAllTeams,
      restorePendingScroll,
      search,
      selectedTags,
      sortBy,
      sortOrder,
      statusFilter,
      updatedFrom,
      updatedTo,
      user?.role
    ]
  );

  useEffect(() => {
    const nextShouldBeSilent = nextFetchSilentRef.current;
    nextFetchSilentRef.current = false;
    if (!nextShouldBeSilent) {
      pendingScrollRestoreRef.current = null;
    }

    const timeoutId = window.setTimeout(() => {
      void fetchFlows({ silent: nextShouldBeSilent });
    }, 250);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [fetchFlows]);

  useEffect(() => {
    if (autoRefreshTick === 0) {
      return;
    }
    void fetchFlows({ silent: true });
  }, [autoRefreshTick, fetchFlows]);

  const visibleTags = useMemo(() => {
    if (showAllTags) {
      return tags;
    }

    const preview = tags.slice(0, TAG_PREVIEW_COUNT);
    if (selectedTags.length === 0) {
      return preview;
    }

    const selectedSet = new Set(selectedTags.map((tag) => tag.toLowerCase()));
    const additionalSelected = tags
      .slice(TAG_PREVIEW_COUNT)
      .filter((entry) => selectedSet.has(entry.name.toLowerCase()));

    const combined = [...preview];
    additionalSelected.forEach((entry) => {
      if (!combined.some((item) => item.name === entry.name)) {
        combined.push(entry);
      }
    });

    return combined;
  }, [showAllTags, tags, TAG_PREVIEW_COUNT, selectedTags]);

  const hiddenTagCount = Math.max(tags.length - TAG_PREVIEW_COUNT, 0);
  const showTagToggle = hiddenTagCount > 0;

  const toggleTag = (tag: string) => {
    setSelectedTags((current) =>
      current.includes(tag) ? current.filter((item) => item !== tag) : [...current, tag]
    );
  };

  const flowOnly = selectedKinds.flow && !selectedKinds.node;
  const nodeOnly = selectedKinds.node && !selectedKinds.flow;
  const viewLabel = nodeOnly ? 'nodes' : flowOnly ? 'flows' : 'entries';
  const toggleKind = (kind: 'flow' | 'node') => {
    setSelectedKinds((current) => {
      const next = { ...current, [kind]: !current[kind] };
      if (!next.flow && !next.node) {
        return current;
      }
      return next;
    });
  };

  const filteredFlows = useMemo(() => {
    let result = flows;
    if (visibilityFilter === 'public') {
      result = result.filter((flow) => flow.visibility !== 'team_restricted');
    } else if (visibilityFilter === 'teams') {
      result = result.filter((flow) => flow.visibility === 'team_restricted');
    }

    if (teamFilterIds.length > 0) {
      result = result.filter(
        (flow) =>
          flow.visibility === 'team_restricted' &&
          Array.isArray(flow.visibleTeamIds) &&
          flow.visibleTeamIds.some((id) => teamFilterIds.includes(id))
      );
    }

    return result;
  }, [flows, visibilityFilter, teamFilterIds]);

  const hasResults = filteredFlows.length > 0;

  const filterChips = useMemo(() => {
    const chips: { label: string; onRemove: () => void }[] = [];
    if (search.trim()) {
      chips.push({ label: `search "${search.trim()}"`, onRemove: () => setSearch('') });
    }
    for (const tag of selectedTags) {
      chips.push({
        label: tag,
        onRemove: () => setSelectedTags((current) => current.filter((t) => t !== tag))
      });
    }
    if (!selectedKinds.flow || !selectedKinds.node) {
      chips.push({
        label: `content: ${flowOnly ? 'flows' : 'nodes'}`,
        onRemove: () => setSelectedKinds({ flow: true, node: true })
      });
    }
    if (visibilityFilter === 'public') {
      chips.push({ label: 'public only', onRemove: () => setVisibilityFilter('all') });
    } else if (visibilityFilter === 'teams') {
      chips.push({ label: 'team-only', onRemove: () => setVisibilityFilter('all') });
    }
    if (teamFilterIds.length > 0) {
      const allKnownTeams = [...myTeams, ...otherTeams];
      const teamLookup = new Map(allKnownTeams.map((team) => [team.id, team.name]));
      for (const id of teamFilterIds) {
        const name = teamLookup.get(id);
        if (name) {
          chips.push({
            label: name,
            onRemove: () => setTeamFilterIds((current) => current.filter((tid) => tid !== id))
          });
        }
      }
    }
    if (recommendedOnly) {
      chips.push({ label: 'recommended', onRemove: () => setRecommendedOnly(false) });
    }
    if (madeByThalesOnly) {
      chips.push({ label: 'Thales only', onRemove: () => setMadeByThalesOnly(false) });
    }
    if (statusFilter && statusFilter !== 'all' && user?.role === 'admin') {
      chips.push({
        label: `status: ${statusFilter}`,
        onRemove: () => setStatusFilter('all')
      });
    }
    return chips;
  }, [
    flowOnly,
    madeByThalesOnly,
    myTeams,
    otherTeams,
    recommendedOnly,
    search,
    selectedKinds.flow,
    selectedKinds.node,
    selectedTags,
    statusFilter,
    teamFilterIds,
    user?.role,
    visibilityFilter
  ]);

  const hasAdvancedFilters =
    authorFilter.trim() !== '' ||
    createdFrom !== '' ||
    createdTo !== '' ||
    updatedFrom !== '' ||
    updatedTo !== '';

  const clearAdvancedFilters = () => {
    setAuthorFilter('');
    setCreatedFrom('');
    setCreatedTo('');
    setUpdatedFrom('');
    setUpdatedTo('');
  };

  const resetAllFilters = () => {
    try {
      sessionStorage.removeItem(FILTERS_STORAGE_KEY);
    } catch {
      // ignore
    }
    setSearch('');
    setSelectedTags([]);
    setSelectedKinds({ flow: true, node: true });
    setAuthorFilter('');
    setCreatedFrom('');
    setCreatedTo('');
    setUpdatedFrom('');
    setUpdatedTo('');
    setShowAdvanced(false);
    setStatusFilter(user?.role === 'admin' ? 'all' : 'approved');
    setShowAllTags(false);
    setRecommendedOnly(false);
    setMadeByThalesOnly(false);
    setVisibilityFilter('all');
    setTeamFilterIds([]);
    setShowOtherTeams(false);
    setSortBy('updatedAt');
    setSortOrder('desc');
    setPage(1);
    setPageSize(PAGE_SIZE_OPTIONS[0]);
    pendingScrollRestoreRef.current = 0;
  };

  const handleViewMode = (mode: 'grid' | 'list') => {
    setViewMode(mode);
    try { localStorage.setItem(VIEW_MODE_KEY, mode); } catch { /* ignore */ }
  };

  const toggleColumn = (colId: ColumnId) => {
    setVisibleColumns((current) => {
      const next = new Set(current);
      if (next.has(colId)) {
        if (next.size <= 1) return current; // keep at least one
        next.delete(colId);
      } else {
        next.add(colId);
      }
      try { localStorage.setItem(LIST_COLUMNS_KEY, JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
  };

  useEffect(() => {
    if (!columnPickerOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (columnPickerRef.current && !columnPickerRef.current.contains(e.target as Node)) {
        setColumnPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [columnPickerOpen]);

  const handlePageSizeChange = (value: number) => {
    setPageSize(value);
    setPage(1);
  };

  const goToPage = (nextPage: number) => {
    const clamped = Math.min(Math.max(nextPage, 1), displayTotalPages);
    nextFetchSilentRef.current = true;
    const anchor = resultScrollAnchorRef.current;
    pendingScrollRestoreRef.current = anchor ? Math.max(anchor.offsetTop - 16, 0) : window.scrollY;
    setPage(clamped);
  };

  const displayTotal = serverTotal;
  const displayTotalPages = Math.max(1, serverTotalPages);
  const pageFirstItem =
    displayTotal === 0 ? 0 : Math.min((page - 1) * pageSize + 1, displayTotal);
  const pageLastItem = displayTotal === 0 ? 0 : Math.min(displayTotal, page * pageSize);

  useEffect(() => {
    if (page > displayTotalPages) {
      setPage(displayTotalPages);
    }
  }, [displayTotalPages, page]);

  const renderPaginationControls = (position: 'top' | 'bottom') => {
    if (displayTotal === 0) {
      return null;
    }

    const selectId = `page-size-${position}`;
    const multiplePages = displayTotalPages > 1;

    return (
      <div className="pagination">
        <div className="pagination__summary">
          Showing {pageFirstItem.toLocaleString()}–{pageLastItem.toLocaleString()} of{' '}
          {displayTotal.toLocaleString()}
        </div>
        <div className="pagination__controls">
          <label className="pagination__page-size" htmlFor={selectId}>
            Per page
            <select
              id={selectId}
              value={pageSize}
              onChange={(event) => handlePageSizeChange(Number(event.target.value))}
            >
              {PAGE_SIZE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          {multiplePages ? (
            <div className="pagination__buttons">
              <button
                type="button"
                className="button-tertiary"
                onClick={() => goToPage(1)}
                disabled={page === 1}
              >
                « First
              </button>
              <button
                type="button"
                className="button-tertiary"
                onClick={() => goToPage(page - 1)}
                disabled={page === 1}
              >
                ‹ Prev
              </button>
              <span className="pagination__page-indicator">
                Page {Math.min(page, displayTotalPages)} of {displayTotalPages}
              </span>
              <button
                type="button"
                className="button-tertiary"
                onClick={() => goToPage(page + 1)}
                disabled={page === displayTotalPages}
              >
                Next ›
              </button>
              <button
                type="button"
                className="button-tertiary"
                onClick={() => goToPage(displayTotalPages)}
                disabled={page === displayTotalPages}
              >
                Last »
              </button>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <div className="marketplace">
      <div className="marketplace__filters">
        <div className="marketplace__filters-header">
          <div className="marketplace__filters-title">
            <h2>Filters</h2>
            {filtersCollapsed && filterChips.length > 0 ? (
              <div className="marketplace__filter-chips">
                {filterChips.map((chip) => (
                  <span key={chip.label} className="filter-chip">
                    <span className="filter-chip__label">{chip.label}</span>
                    <button
                      type="button"
                      className="filter-chip__remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        chip.onRemove();
                      }}
                      aria-label={`Remove filter: ${chip.label}`}
                    >
                      &times;
                    </button>
                  </span>
                ))}
                <button
                  type="button"
                  className="filter-chip__clear-all"
                  onClick={resetAllFilters}
                >
                  Clear all
                </button>
              </div>
            ) : null}
            {filtersCollapsed && filterChips.length === 0 ? (
              <span className="marketplace__summary-inline">No filters applied</span>
            ) : null}
          </div>
          <button
            type="button"
            className="button-tertiary"
            onClick={() => setFiltersCollapsed((current) => !current)}
          >
            {filtersCollapsed ? 'Show filters' : 'Hide filters'}
          </button>
        </div>

        {!filtersCollapsed ? (
          <>
            <div className="marketplace__search">
              <label htmlFor="search">Search {viewLabel}</label>
              <input
                id="search"
                type="search"
                placeholder={`Search ${viewLabel} by title, description, or JSON...`}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            <div className="marketplace__filter-actions">
              <button type="button" className="button button-secondary" onClick={resetAllFilters}>
                Reset filters
              </button>
            </div>

            <div className="marketplace__sort">
              <div className="marketplace__sort-field">
                <label htmlFor="sort-field">Sort by</label>
                <select
                  id="sort-field"
                  value={sortBy}
                  onChange={(event) => setSortBy(event.target.value as typeof sortBy)}
                >
                  <option value="updatedAt">Last updated</option>
                  <option value="createdAt">Created date</option>
                  <option value="title">Name</option>
                  <option value="upvotes">Most upvoted</option>
                </select>
              </div>
              <div className="marketplace__sort-field">
                <label htmlFor="sort-order">Order</label>
                <select
                  id="sort-order"
                  value={sortOrder}
                  onChange={(event) => setSortOrder(event.target.value as typeof sortOrder)}
                >
                  <option value="desc">Descending</option>
                  <option value="asc">Ascending</option>
                </select>
              </div>
            </div>

            <div className="marketplace__kind-toggle">
              <span>Content</span>
              <div className="tag-group">
                {(['flow', 'node'] as const).map((kindOption) => {
                  const isSelected = selectedKinds[kindOption];
                  return (
                    <button
                      type="button"
                      key={kindOption}
                      className={`chip chip-toggle ${isSelected ? 'chip-selected' : ''}`}
                      onClick={() => toggleKind(kindOption)}
                    >
                      <span className="chip-toggle__checkbox" aria-hidden="true">
                        {isSelected ? '✓' : ''}
                      </span>
                      {kindOption === 'flow' ? 'Flows' : 'Nodes'}
                    </button>
                  );
                })}
              </div>
            </div>

            {tags.length > 0 ? (
              <div className="marketplace__tags">
                <span>Tags</span>
                <div className="tag-group">
                  {visibleTags.map((tag) => (
                    <button
                      type="button"
                      key={tag.name}
                      className={`chip ${selectedTags.includes(tag.name) ? 'chip-selected' : ''}`}
                      onClick={() => toggleTag(tag.name)}
                    >
                      {tag.name}
                      <span className="tag-chip__count">({tag.count})</span>
                    </button>
                  ))}
                  {showTagToggle ? (
                    <button
                      type="button"
                      className="button-tertiary tag-group__more"
                      onClick={() => setShowAllTags((current) => !current)}
                    >
                      {showAllTags
                        ? 'Show fewer tags'
                        : `Show more tags${hiddenTagCount > 0 ? ` (+${hiddenTagCount})` : ''}`}
                    </button>
                  ) : null}
                </div>
              </div>
            ) : null}

        {user?.role === 'admin' ? (
          <div className="marketplace__status">
            <label htmlFor="status-filter">Status</label>
            <select
              id="status-filter"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)}
            >
              <option value="all">All</option>
              <option value="approved">Approved</option>
              <option value="pending">Pending</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        ) : null}

        <div className="marketplace__recommended">
          <label htmlFor="recommended-only" className="checkbox-field">
            <input
              id="recommended-only"
              type="checkbox"
              checked={recommendedOnly}
              onChange={(event) => setRecommendedOnly(event.target.checked)}
            />
            Show recommended only
          </label>
        </div>

        <div className="marketplace__thales">
          <label htmlFor="thales-only" className="checkbox-field">
            <input
              id="thales-only"
              type="checkbox"
              checked={madeByThalesOnly}
              onChange={(event) => setMadeByThalesOnly(event.target.checked)}
            />
            Show “Made by Thales” only
          </label>
        </div>

        <div className="marketplace__visibility">
          <label>Visibility</label>
          <div className="tag-group">
            {[
              { value: 'all', label: 'Public + my teams' },
              { value: 'public', label: 'Public only' },
              { value: 'teams', label: 'Team-only' }
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                className={`chip chip-toggle ${visibilityFilter === option.value ? 'chip-selected' : ''}`}
                onClick={() => setVisibilityFilter(option.value as typeof visibilityFilter)}
              >
                <span className="chip-toggle__checkbox" aria-hidden="true">
                  {visibilityFilter === option.value ? '✓' : ''}
                </span>
                {option.label}
              </button>
            ))}
          </div>
          {myTeams.length > 0 ? (
            <div className="marketplace__team-chips">
              <span className="hint">Your teams (click to filter):</span>
              <div className="tag-group">
                {myTeams.map((team) => {
                  const isSelected = teamFilterIds.includes(team.id);
                  return (
                    <button
                      key={team.id}
                      type="button"
                      className={`chip chip-ghost${isSelected ? ' chip-selected' : ''}`}
                      onClick={() =>
                        setTeamFilterIds((current) =>
                          current.includes(team.id)
                            ? current.filter((id) => id !== team.id)
                            : [...current, team.id]
                        )
                      }
                    >
                      {team.name}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
          {otherTeams.length > 0 ? (
            <div className="marketplace__team-chips">
              <label className="checkbox-field">
                <input
                  type="checkbox"
                  checked={showOtherTeams}
                  onChange={(event) => setShowOtherTeams(event.target.checked)}
                />
                Show other teams
              </label>
              {showOtherTeams ? (
                <div className="tag-group">
                  {otherTeams.map((team) => {
                    const isSelected = teamFilterIds.includes(team.id);
                    return (
                      <button
                        key={team.id}
                        type="button"
                        className={`chip chip-ghost${isSelected ? ' chip-selected' : ''}`}
                        onClick={() =>
                          setTeamFilterIds((current) =>
                            current.includes(team.id)
                              ? current.filter((id) => id !== team.id)
                              : [...current, team.id]
                          )
                        }
                      >
                        {team.name}
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>
          ) : null}
          {teamFilterIds.length > 0 ? (
            <button
              type="button"
              className="button-tertiary marketplace__clear-teams"
              onClick={() => setTeamFilterIds([])}
            >
              Clear team filter
            </button>
          ) : null}
        </div>

        <div className="marketplace__advanced-toggle">
          <button
            className="button button-secondary"
            type="button"
            onClick={() => setShowAdvanced((current) => !current)}
          >
            {showAdvanced ? 'Hide advanced filters' : 'Show advanced filters'}
          </button>
        </div>

            {showAdvanced ? (
              <div className="marketplace__advanced">
                <div className="marketplace__field">
                  <label htmlFor="author-filter">Author</label>
                  <input
                    id="author-filter"
                    type="search"
                    placeholder="Author name..."
                    value={authorFilter}
                    onChange={(event) => setAuthorFilter(event.target.value)}
                  />
                </div>

                <div className="marketplace__dates">
                  <div className="marketplace__field">
                    <label htmlFor="created-from">Created from</label>
                    <input
                      id="created-from"
                      type="date"
                      value={createdFrom}
                      onChange={(event) => setCreatedFrom(event.target.value)}
                    />
                  </div>
                  <div className="marketplace__field">
                    <label htmlFor="created-to">Created to</label>
                    <input
                      id="created-to"
                      type="date"
                      value={createdTo}
                      onChange={(event) => setCreatedTo(event.target.value)}
                    />
                  </div>
                  <div className="marketplace__field">
                    <label htmlFor="updated-from">Updated from</label>
                    <input
                      id="updated-from"
                      type="date"
                      value={updatedFrom}
                      onChange={(event) => setUpdatedFrom(event.target.value)}
                    />
                  </div>
                  <div className="marketplace__field">
                    <label htmlFor="updated-to">Updated to</label>
                    <input
                      id="updated-to"
                      type="date"
                      value={updatedTo}
                      onChange={(event) => setUpdatedTo(event.target.value)}
                    />
                  </div>
                </div>

                <div className="marketplace__actions">
                  <button
                    className="button button-tertiary"
                    type="button"
                    onClick={clearAdvancedFilters}
                    disabled={!hasAdvancedFilters}
                  >
                    Clear filters
                  </button>
                </div>
              </div>
            ) : null}
          </>
        ) : null}
      </div>

      <div ref={resultScrollAnchorRef} aria-hidden="true" />

      <div className="marketplace__toolbar">
        {renderPaginationControls('top')}
        <div className="marketplace__view-toggle">
          <button
            type="button"
            className={`marketplace__view-btn${viewMode === 'grid' ? ' marketplace__view-btn--active' : ''}`}
            onClick={() => handleViewMode('grid')}
            aria-label="Grid view"
            title="Grid view"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="1" y="1" width="6.5" height="6.5" rx="1.5" />
              <rect x="10.5" y="1" width="6.5" height="6.5" rx="1.5" />
              <rect x="1" y="10.5" width="6.5" height="6.5" rx="1.5" />
              <rect x="10.5" y="10.5" width="6.5" height="6.5" rx="1.5" />
            </svg>
          </button>
          <button
            type="button"
            className={`marketplace__view-btn${viewMode === 'list' ? ' marketplace__view-btn--active' : ''}`}
            onClick={() => handleViewMode('list')}
            aria-label="List view"
            title="List view"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5">
              <line x1="1" y1="3" x2="17" y2="3" />
              <line x1="1" y1="7.5" x2="17" y2="7.5" />
              <line x1="1" y1="12" x2="17" y2="12" />
              <line x1="1" y1="16.5" x2="17" y2="16.5" />
            </svg>
          </button>
          {viewMode === 'list' ? (
            <div className="marketplace__column-picker" ref={columnPickerRef}>
              <button
                type="button"
                className="marketplace__column-picker-btn"
                onClick={() => setColumnPickerOpen((o) => !o)}
                aria-label="Choose columns"
                title="Choose columns"
              >
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M6 1v16M12 1v16M1 6h16M1 12h16" />
                </svg>
              </button>
              {columnPickerOpen ? (
                <div className="marketplace__column-menu">
                  {ALL_LIST_COLUMNS.map((col) => (
                    <label key={col.id}>
                      <input
                        type="checkbox"
                        checked={visibleColumns.has(col.id)}
                        onChange={() => toggleColumn(col.id)}
                      />
                      {col.label}
                    </label>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {loading ? <div className="loading">Loading {viewLabel}…</div> : null}
      {error ? <div className="alert alert-error">{error}</div> : null}

      {!loading && !error ? (
        hasResults ? (
          viewMode === 'grid' ? (
            <div className="marketplace__grid">
              {filteredFlows.map((flow) => (
                <FlowCard
                  key={flow.id}
                  flow={flow}
                  linkState={marketplaceLinkState}
                  onNavigate={persistScrollPosition}
                />
              ))}
            </div>
          ) : (
            <div className="marketplace__list-wrapper">
              <table className="marketplace__list-table">
                <thead>
                  <tr>
                    {visibleColumns.has('title') ? <th>Title</th> : null}
                    {visibleColumns.has('kind') ? <th>Kind</th> : null}
                    {visibleColumns.has('type') ? <th>Type</th> : null}
                    {visibleColumns.has('visibility') ? <th>Visibility</th> : null}
                    {visibleColumns.has('owner') ? <th>Owner</th> : null}
                    {visibleColumns.has('updated') ? <th>Updated</th> : null}
                    {visibleColumns.has('status') ? <th>Status</th> : null}
                    {visibleColumns.has('labels') ? <th>Labels</th> : null}
                    {visibleColumns.has('upvotes') ? <th>Upvotes</th> : null}
                    {visibleColumns.has('tags') ? <th>Tags</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {filteredFlows.map((flow) => (
                    <tr key={flow.id}>
                      {visibleColumns.has('title') ? (
                        <td>
                          <Link
                            to={`/flows/${flow.id}`}
                            state={marketplaceLinkState}
                            onClickCapture={persistScrollPosition}
                          >
                            {flow.title}
                          </Link>
                        </td>
                      ) : null}
                      {visibleColumns.has('kind') ? (
                        <td>
                          <span className={`flow-card__kind-badge flow-card__kind-badge--${flow.kind === 'node' ? 'node' : 'flow'}`}>
                            {flow.kind === 'node' ? 'Node' : 'Flow'}
                          </span>
                        </td>
                      ) : null}
                      {visibleColumns.has('type') ? (
                        <td>
                          {flow.flowType || '—'}
                          {flow.flowTypeDetail ? ` — ${flow.flowTypeDetail}` : ''}
                        </td>
                      ) : null}
                      {visibleColumns.has('visibility') ? (
                        <td>
                          <span
                            className={`flow-card__visibility flow-card__visibility--${
                              flow.visibility === 'team_restricted' ? 'team' : 'public'
                            }`}
                          >
                            {flow.visibility === 'team_restricted'
                              ? flow.visibleTeams?.map((t) => t.name).join(', ') || 'Team restricted'
                              : 'Public'}
                          </span>
                        </td>
                      ) : null}
                      {visibleColumns.has('owner') ? (
                        <td>{flow.createdByName || '—'}</td>
                      ) : null}
                      {visibleColumns.has('updated') ? (
                        <td>{new Date(flow.updatedAt).toLocaleDateString()}</td>
                      ) : null}
                      {visibleColumns.has('status') ? (
                        <td><span className={`status status-${flow.status}`}>{flow.status}</span></td>
                      ) : null}
                      {visibleColumns.has('labels') ? (
                        <td>
                          <div className="marketplace__list-labels">
                            {flow.isRecommended ? (
                              <span className="flow-card__badge flow-card__badge--recommended">Recommended</span>
                            ) : null}
                            {flow.madeByThales ? (
                              <span className="flow-card__badge flow-card__badge--thales">Made by Thales</span>
                            ) : null}
                            {!flow.isRecommended && !flow.madeByThales ? '—' : null}
                          </div>
                        </td>
                      ) : null}
                      {visibleColumns.has('upvotes') ? (
                        <td>
                          {(flow.upvoteCount ?? 0).toLocaleString()}
                          {flow.viewerHasUpvoted ? ' (You)' : ''}
                        </td>
                      ) : null}
                      {visibleColumns.has('tags') ? (
                        <td>
                          {flow.tags?.length ? (
                            <div className="marketplace__list-tags">
                              {flow.tags.map((tag) => (
                                <span key={tag} className="tag">{tag}</span>
                              ))}
                            </div>
                          ) : '—'}
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : (
          <div className="empty-state">
            <h3>No {viewLabel} found</h3>
            <p>
              Try different search terms or contribute a new{' '}
              {nodeOnly ? 'node' : flowOnly ? 'flow' : 'entry'}.
            </p>
          </div>
        )
      ) : null}

      {renderPaginationControls('bottom')}
    </div>
  );
}
